// src/components/BarIndicator/BarIndicator.jsx
import React, { useEffect, useState, useMemo } from 'react';
import {
    BarIndicatorContainer,
    BarIndicatorWrapper,
    Tab,
    BarTitle,
    LabelContainer,
    LabelItem,
} from './BarIndicatorStyle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Tooltip from '@mui/material/Tooltip';

const BarIndicator = React.memo(
    ({
         filterType,
         // Para "categoria" sí mostramos umbrales numéricos (pos/neg)
         categoryRanges = { pos: 0, neg: 0 },
         // Se deja por compatibilidad pero NO se usa para mostrar números
         ranges = {},
         onLabelClick = () => {},
         setLabelsAndColors = () => {},
         harvestLabels = null,
         harvestTitle = '',
         isHistory = false,
     }) => {
        const [isOpen, setIsOpen] = useState(true);

        /* Ocultar/mostrar según scroll (25 %) */
        useEffect(() => {
            const container = document.getElementById('content-wrapper');
            const scrollElem = container || window;

            const handleScroll = () => {
                let scrollTop, scrollHeight, clientHeight;
                if (container) {
                    scrollTop = container.scrollTop;
                    scrollHeight = container.scrollHeight;
                    clientHeight = container.clientHeight;
                } else {
                    scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                    scrollHeight = document.documentElement.scrollHeight;
                    clientHeight = document.documentElement.clientHeight;
                }
                const ratio = scrollHeight <= clientHeight ? 0 : scrollTop / (scrollHeight - clientHeight);

                setIsOpen((prev) => {
                    if (ratio > 0.25 && prev) return false;
                    if (ratio <= 0.25 && !prev) return true;
                    return prev;
                });
            };

            scrollElem.addEventListener('scroll', handleScroll, { passive: true });
            return () => scrollElem.removeEventListener('scroll', handleScroll);
        }, []);

        /* Etiquetas estándar: SIEMPRE texto (Bajo/Medio/Alto) */
        const std3 = () => [
            { color: '#4caf50', text: 'Bajo', textColor: '#ffffff' },
            { color: '#ffeb3b', text: 'Medio', textColor: '#000000' },
            { color: '#f44336', text: 'Alto', textColor: '#ffffff' },
        ];

        /* Selección de título y etiquetas por tipo de filtro */
        const getTitleAndLabels = (type) => {
            switch (type) {
                /* ─── Variables de maquinaria: texto ─── */
                case 'speed':
                    return { title: 'Velocidad', labels: std3() };
                case 'gpsQuality':
                    return { title: 'Calidad GPS', labels: std3() };
                case 'fuel':
                    return { title: 'Combustible', labels: std3() };
                case 'cutterBase':
                    return { title: 'Presión Cortador Base', labels: std3() };
                case 'rpm':
                    return { title: 'RPM', labels: std3() };

                case 'autoPilot':
                    return {
                        title: 'Piloto Automático',
                        labels: [
                            { color: 'green', text: 'Enganchado', textColor: '#ffffff' },
                            { color: 'red', text: 'Desenganchado', textColor: '#ffffff' },
                        ],
                    };

                case 'modeCutterBase':
                    return {
                        title: 'Modo de corte base',
                        labels: [
                            { color: 'green', text: 'Automático', textColor: '#ffffff' },
                            { color: 'red', text: 'Manual', textColor: '#ffffff' },
                        ],
                    };

                case 'autoTracket':
                    return {
                        title: 'Auto Tracket',
                        labels: [
                            { color: 'green', text: 'Enganchado', textColor: '#ffffff' },
                            { color: 'red', text: 'Desenganchado', textColor: '#ffffff' },
                        ],
                    };

                /* ─── Mapas de área ─── */
                case 'aplicacionesAreas':
                    return {
                        title: 'Aplicaciones Áreas',
                        labels: [
                            { color: 'green', text: 'Área aplicada', textColor: '#ffffff' },
                            { color: 'red', text: 'Área sobre aplicada', textColor: '#ffffff' },
                        ],
                    };

                /* ─── Sensores genéricos: texto ─── */
                case 'altura':
                    return { title: 'Altura', labels: std3() };
                case 'hdop':
                    return { title: 'HDOP', labels: std3() };

                /* ─── Fertilización: **TEXTO** (no números) ─── */
                case 'p1dr':
                    return { title: 'Dosis Real P1', labels: std3() };
                case 'p1dt':
                    return { title: 'Dosis Teórica P1', labels: std3() };
                case 'p2dr':
                    return { title: 'Dosis Real P2', labels: std3() };
                case 'dosisReal':
                    return { title: 'Dosis Real', labels: std3() };

                /* ─── Categorías: aquí SÍ mostramos umbrales numéricos ─── */
                case 'categoria': {
                    const pos = Number(categoryRanges?.pos ?? 0);
                    const neg = Number(categoryRanges?.neg ?? 0);
                    return {
                        title: 'Categoría de aplicación',
                        labels: [
                            {
                                color: '#f44336',
                                text: `Sobre aplicado (> ${Number.isFinite(pos) ? pos.toFixed(2) : 0})`,
                                textColor: '#ffffff',
                            },
                            { color: '#4caf50', text: 'Bien aplicado (= 0)', textColor: '#ffffff' },
                            {
                                color: '#ffeb3b',
                                text: `Sub aplicado (< ${Number.isFinite(neg) ? neg.toFixed(2) : 0})`,
                                textColor: '#000000',
                            },
                        ],
                    };
                }

                /* ─── Planificación de cosecha ─── */
                case 'harvestGrouping':
                    if (harvestLabels && harvestLabels.length > 0) {
                        return {
                            title: harvestTitle || 'Planificación cosecha',
                            labels: harvestLabels.map((l) => ({ ...l, textColor: '#ffffff' })),
                        };
                    }
                    return {
                        title: 'Planificación cosecha',
                        labels: [
                            { color: '#999999', text: 'Valor 1', textColor: '#ffffff' },
                            { color: '#bbbbbb', text: 'Valor 2', textColor: '#ffffff' },
                        ],
                    };

                /* ─── Traslape ─── */
                case 'traslape':
                case 'aerialApplicationsTraslape':
                    return {
                        title: 'Leyenda',
                        labels: [
                            { color: 'green', text: 'Aplicado', textColor: '#ffffff' },
                            { color: 'red', text: 'Sobre aplicado', textColor: '#ffffff' },
                            { color: '#f1d700', text: 'No aplicado', textColor: '#ffffff' },
                        ],
                    };
                case 'aerialApplicationsMalezaVar':
                    return {
                        title: 'Aplicación localizada en maleza',
                        labels: [
                            { color: '#00ACC1', text: 'Maleza detectada', textColor: '#ffffff' },
                            { color: 'green', text: 'Línea aplicada en maleza', textColor: '#ffffff' },
                            { color: 'red', text: 'Línea aplicada fuera de maleza', textColor: '#ffffff' },
                            { color: '#7E57C2', text: 'Maleza no aplicada', textColor: '#ffffff' },
                        ],
                    };
                // VRA con franjas en polígonos (Herbicidas y APS con avioneta): la
                // aplicación no son líneas, así que la leyenda no habla de «Línea».
                case 'aerialApplicationsMalezaVarFranjas':
                    return {
                        title: 'Aplicación localizada en maleza',
                        labels: [
                            { color: '#00ACC1', text: 'Maleza detectada', textColor: '#ffffff' },
                            { color: 'green', text: 'Aplicada en maleza', textColor: '#ffffff' },
                            { color: 'red', text: 'Aplicada fuera de maleza', textColor: '#ffffff' },
                            { color: '#7E57C2', text: 'Maleza no aplicada', textColor: '#ffffff' },
                        ],
                    };
                case 'aerialApplicationsTraslapeDrones':
                    return {
                        title: 'Leyenda',
                        labels: [
                            { color: 'green', text: 'Aplicado', textColor: '#ffffff' },
                            { color: 'red', text: 'Sobre aplicado', textColor: '#ffffff' },
                            { color: 'purple', text: 'Línea de aplicación', textColor: '#ffffff' },
                            { color: '#ffffff', text: 'Ruta de vuelo', textColor: '#000000' },
                            { color: '#f1d700', text: 'No aplicado', textColor: '#ffffff' },
                        ],
                    };

                /* ─── Pisoteo ─── */
                case 'trampling':
                    return {
                        title: 'Pisoteo',
                        labels: [
                            { color: '#ffffff', text: '0%', textColor: '#ffffff' },
                            { color: '#ffe5e5', text: '25%', textColor: '#ffffff' },
                            { color: '#ffcccc', text: '50%', textColor: '#ffffff' },
                            { color: '#ff9999', text: '75%', textColor: '#ffffff' },
                            { color: '#ff0000', text: '100%', textColor: '#ffffff' },
                        ],
                    };
                case 'fertilizacionDefault':
                    return {
                        title: 'Leyenda',
                        labels: [
                            { color: '#4caf50', text: 'Aplicado',      textColor: '#ffffff' }, // verde
                            { color: '#f1d700', text: 'No aplicado',   textColor: '#000000' }, // amarillo
                            { color: '#f44336', text: 'Sobre aplicado', textColor: '#ffffff' }, // rojo
                        ],
                    };
                /* ─── Default ─── */
                default:
                    return { title: 'Indicador', labels: std3() };
            }
        };

        const { title, labels } = useMemo(
            () => getTitleAndLabels(filterType),
            [filterType, harvestLabels, harvestTitle, categoryRanges, ranges]
        );

        /* Sincronizar con el padre */
        useEffect(() => {
            if (!setLabelsAndColors) return;
            if (title === '' && labels.length === 0) return;
            setLabelsAndColors((prev) => {
                if (prev.title === title && JSON.stringify(prev.labels) === JSON.stringify(labels)) {
                    return prev;
                }
                return { title, labels };
            });
        }, [title, labels, setLabelsAndColors]);

        return (
            <BarIndicatorContainer>
                <BarIndicatorWrapper isOpen={isOpen}>
                    <BarTitle>{title}</BarTitle>
                    <LabelContainer>
                        {labels.map((label, idx) => (
                            <LabelItem
                                key={idx}
                                style={{ backgroundColor: label.color, color: label.textColor }}
                                onClick={() => onLabelClick(label.text, label.color)}
                            >
                                {label.text}
                            </LabelItem>
                        ))}
                    </LabelContainer>
                </BarIndicatorWrapper>
                <Tooltip title={isOpen ? 'Ocultar leyenda' : 'Mostrar leyenda'}>
                    <Tab onClick={() => setIsOpen(!isOpen)} isOpen={isOpen}>
                        {isOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                    </Tab>
                </Tooltip>
            </BarIndicatorContainer>
        );
    }
);

export default BarIndicator;
